import { useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import api from '../../services/axiosInterceptor.js';
import useApiData from '../../services/useApiData.js';

const NEW_QUESTION = '__new_question__';
const NEW_GRADE = '__new_grade__';
const NEW_VARIANT = '__new_variant__';
const NEW_LISTING = '__new_listing__';
const NEW_ADD_TO_BUNDLE = '__new_add_to_bundle__';
const NEW_CATALOG_FIELD = '__new_catalog_field__';
const NEW_SHELF = '__new_shelf__';
const NEW_SUBFLOW = '__new_subflow__';

function catalogFieldHasValue(value) {
    if (Array.isArray(value)) return value.length > 0;
    return Boolean(String(value ?? '').trim());
}

function shelfDraftReady(draft) {
    return Boolean(String(draft?.shelfId || '').trim());
}

function ShelfValueEditor({ shelves, shelfId, shelfBinId, onChange, size = 'small' }) {
    const selectedShelf =
        (shelves || []).find((s) => String(s.id) === String(shelfId || '')) || null;
    const bins = selectedShelf?.bins || [];

    return (
        <>
            <TextField
                select
                fullWidth
                size={size}
                margin="dense"
                label="Shelf"
                value={shelfId || ''}
                onChange={(e) => {
                    const nextId = e.target.value;
                    const shelf = (shelves || []).find((s) => String(s.id) === String(nextId));
                    onChange({
                        shelfId: nextId,
                        shelfBinId: '',
                        shelfName: shelf?.shelfName || shelf?.name || '',
                    });
                }}
            >
                <MenuItem value="">
                    <em>Select shelf…</em>
                </MenuItem>
                {(shelves || []).map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                        {s.shelfName || s.name || s.id}
                    </MenuItem>
                ))}
            </TextField>
            {bins.length > 0 && (
                <TextField
                    select
                    fullWidth
                    size={size}
                    margin="dense"
                    label="Bin (optional)"
                    value={shelfBinId || ''}
                    onChange={(e) =>
                        onChange({
                            shelfId: shelfId || '',
                            shelfBinId: e.target.value,
                            shelfName: selectedShelf?.shelfName || selectedShelf?.name || '',
                        })
                    }
                >
                    <MenuItem value="">
                        <em>No bin</em>
                    </MenuItem>
                    {bins.map((bin) => (
                        <MenuItem key={bin.id} value={bin.id}>
                            {bin.name || bin.id}
                        </MenuItem>
                    ))}
                </TextField>
            )}
        </>
    );
}

function CatalogFieldValueEditor({ fields, fieldName, value, onFieldChange, onValueChange, size = 'small' }) {
    const selectedField =
        (fields || []).find((field) => field.name === fieldName) || null;

    return (
        <>
            <Autocomplete
                options={fields || []}
                value={selectedField}
                onChange={(_, field) => {
                    // onFieldChange owns the value reset — calling onValueChange here too
                    // races two stale node updates and clears catalogFieldName.
                    onFieldChange(field?.name || '');
                }}
                getOptionLabel={(field) => String(field?.name || '')}
                isOptionEqualToValue={(a, b) =>
                    a?.id === b?.id || a?.mongoPath === b?.mongoPath || a?.name === b?.name
                }
                renderInput={(params) => (
                    <TextField {...params} size={size} label="Item ID Catalog field" margin="dense" />
                )}
            />
            {selectedField?.type === 'singleSelect' && (
                <TextField
                    select
                    fullWidth
                    size={size}
                    margin="dense"
                    label="Value"
                    value={value || ''}
                    onChange={(e) => onValueChange(e.target.value)}
                >
                    <MenuItem value="">
                        <em>Select…</em>
                    </MenuItem>
                    {(selectedField.choices || []).map((choice) => (
                        <MenuItem key={choice} value={choice}>
                            {choice}
                        </MenuItem>
                    ))}
                </TextField>
            )}
            {selectedField?.type === 'multipleSelects' && (
                <Autocomplete
                    multiple
                    options={selectedField.choices || []}
                    value={Array.isArray(value) ? value : []}
                    onChange={(_, next) => onValueChange(next)}
                    getOptionLabel={(option) => String(option ?? '')}
                    renderInput={(params) => (
                        <TextField {...params} size={size} label="Value" margin="dense" />
                    )}
                />
            )}
            {selectedField &&
                selectedField.type !== 'singleSelect' &&
                selectedField.type !== 'multipleSelects' && (
                    <TextField
                        fullWidth
                        size={size}
                        margin="dense"
                        label="Value"
                        type={selectedField.type === 'date' ? 'date' : 'text'}
                        InputLabelProps={
                            selectedField.type === 'date' ? { shrink: true } : undefined
                        }
                        value={value || ''}
                        onChange={(e) => onValueChange(e.target.value)}
                    />
                )}
        </>
    );
}

function nodeLabel(node) {
    if (!node) return '';
    if (node.type === 'terminal') {
        return `Grade: ${node.title || node.result?.condition || node.id}`;
    }
    if (node.type === 'action') {
        const kind = node.actionKind || 'productGrade';
        const prefix =
            kind === 'variant'
                ? 'Variant'
                : kind === 'listingField'
                  ? 'Reassign SKU'
                  : kind === 'addToBundle'
                    ? 'Add to bundle'
                    : kind === 'catalogField'
                      ? 'Catalog field'
                      : kind === 'shelf'
                        ? 'Shelf'
                        : kind === 'subflow'
                          ? 'Wizard'
                          : 'Grade';
        const detail =
            kind === 'shelf'
                ? node.shelfName || node.title || node.shelfId || node.id
                : node.title || node.value || node.id;
        return `${prefix}: ${detail}`;
    }
    return `Question: ${node.title || node.prompt || node.id}`;
}

function branchDisplay(branch, inputType) {
    if (branch.label) return branch.label;
    if (inputType === 'boolean') {
        return branch.value === true || branch.value === 'true' ? 'Yes' : 'No';
    }
    return String(branch.value);
}

function uniqueOptions(list) {
    const seen = new Map();
    for (const raw of list || []) {
        const s = String(raw ?? '').trim();
        if (!s) continue;
        const key = s.toLowerCase();
        if (!seen.has(key)) seen.set(key, s);
    }
    return [...seen.values()];
}

export default function NodeInspector({
    tree,
    selectedId,
    onChangeNode,
    onSelect,
    onAddResult,
    onAddAction,
    onAddQuestion,
    onLinkBranchToNewResult,
    onLinkBranchToNewAction,
    onLinkBranchToNewQuestion,
    onInsertQuestionBetween,
    onLinkActionToNewQuestion,
    onLinkActionToNewAction,
}) {
    const nodes = tree?.nodes || {};
    const node = selectedId ? nodes[selectedId] : null;
    const [stepQuery, setStepQuery] = useState('');
    const [newAnswer, setNewAnswer] = useState(null);
    const [dragAnswerIndex, setDragAnswerIndex] = useState(null);
    const [saveForReuse, setSaveForReuse] = useState(false);
    const [pendingKind, setPendingKind] = useState(null);
    const [pendingBranch, setPendingBranch] = useState(null);
    const [pendingValue, setPendingValue] = useState(null);
    const [standaloneKind, setStandaloneKind] = useState('productGrade');
    const [standaloneValue, setStandaloneValue] = useState(null);
    const [catalogFieldDraft, setCatalogFieldDraft] = useState({ name: '', value: '' });
    const [shelfDraft, setShelfDraft] = useState({ shelfId: '', shelfBinId: '', shelfName: '' });

    const categoryScope = tree?.category || '';
    const { data: productGrades } = useApiData('api/broylesllc/inspection-trees/meta/product-grades');
    const { data: publishedTrees } = useApiData('api/broylesllc/inspection-trees?status=published');
    const { data: catalogFields } = useApiData('api/broylesllc/inspection-trees/meta/catalog-fields');
    const { data: shelfOptions } = useApiData('api/broylesllc/shelf-options');
    const catalogFieldOptions = useMemo(
        () => (Array.isArray(catalogFields) ? catalogFields : []),
        [catalogFields]
    );
    const shelves = useMemo(
        () => (Array.isArray(shelfOptions) ? shelfOptions : []),
        [shelfOptions]
    );

    const stepEndpoint = useMemo(() => {
        const q = stepQuery.trim();
        const params = new URLSearchParams();
        if (q) params.set('q', q);
        if (categoryScope) params.set('category', categoryScope);
        if (tree?._id) params.set('treeId', tree._id);
        const qs = params.toString();
        return `api/broylesllc/inspection-trees/meta/step-options${qs ? `?${qs}` : ''}`;
    }, [stepQuery, categoryScope, tree?._id]);
    const { data: stepOptions, refetch: refetchSteps } = useApiData(stepEndpoint);

    const gradeOptions = useMemo(
        () => uniqueOptions(Array.isArray(productGrades) ? productGrades : []),
        [productGrades]
    );
    const answerOptions = useMemo(() => {
        const saved = Array.isArray(stepOptions) ? stepOptions : [];
        const fromNode = (node?.options || []).map(String);
        return uniqueOptions([...saved, ...fromNode, 'Yes', 'No']);
    }, [stepOptions, node]);

    const questionOptions = Object.values(nodes).filter((n) => n?.type === 'question');
    const actionOptions = Object.values(nodes).filter(
        (n) => n?.type === 'action' || n?.type === 'terminal'
    );
    const wizardOptions = useMemo(
        () =>
            (Array.isArray(publishedTrees) ? publishedTrees : []).filter(
                (t) => String(t._id) !== String(tree?._id)
            ),
        [publishedTrees, tree?._id]
    );

    const createToolbar = (
        <div className="insp-section" style={{ borderBottom: '1px solid #eee', paddingBottom: 16 }}>
            <h4>Add to this tree</h4>
            <p className="hint">
                Questions ask the operator. Results assign a field (grade, variant, rename) and can
                optionally continue. “New wizard” hands off to another published tree.
            </p>
            <TextField
                select
                fullWidth
                size="small"
                label="What to add"
                value={standaloneKind}
                onChange={(e) => {
                    setStandaloneKind(e.target.value);
                    setStandaloneValue(null);
                    setCatalogFieldDraft({ name: '', value: '' });
                    setShelfDraft({ shelfId: '', shelfBinId: '', shelfName: '' });
                }}
                sx={{ mb: 1 }}
            >
                <MenuItem value="question">New question</MenuItem>
                <MenuItem value="productGrade">New result (Product Grade)</MenuItem>
                <MenuItem value="variant">New result (Variant)</MenuItem>
                <MenuItem value="listingField">New result (Reassign SKU)</MenuItem>
                <MenuItem value="addToBundle">New result (Add to bundle)</MenuItem>
                <MenuItem value="catalogField">New result (Catalog field)</MenuItem>
                <MenuItem value="shelf">New result (Shelf)</MenuItem>
                <MenuItem value="subflow">New wizard (subflow)</MenuItem>
            </TextField>
            {standaloneKind === 'productGrade' && (
                <Autocomplete
                    options={gradeOptions}
                    value={standaloneValue}
                    onChange={(_, v) => setStandaloneValue(v)}
                    getOptionLabel={(o) => String(o ?? '')}
                    renderInput={(params) => (
                        <TextField {...params} size="small" label="Product Grade" />
                    )}
                />
            )}
            {standaloneKind === 'variant' && (
                <p className="hint" style={{ margin: '4px 0 0' }}>
                    Operator will pick Item ID&apos;s Variant from Product variant cards during the
                    wizard. No preset value needed.
                </p>
            )}
            {standaloneKind === 'listingField' && (
                <p className="hint" style={{ margin: '4px 0 0' }}>
                    Operator will pick the correct SKU via autocomplete during the wizard (misbox
                    correction). No preset value needed.
                </p>
            )}
            {standaloneKind === 'addToBundle' && (
                <p className="hint" style={{ margin: '4px 0 0' }}>
                    Operator will pick a Product SKU, then choose an existing bundle parent or create
                    a new one during the wizard. No preset value needed.
                </p>
            )}
            {standaloneKind === 'catalogField' && (
                <CatalogFieldValueEditor
                    fields={catalogFieldOptions}
                    fieldName={catalogFieldDraft.name}
                    value={catalogFieldDraft.value}
                    onFieldChange={(name) => {
                        const field = catalogFieldOptions.find((f) => f.name === name);
                        setCatalogFieldDraft({
                            name,
                            value: field?.type === 'multipleSelects' ? [] : '',
                        });
                    }}
                    onValueChange={(value) =>
                        setCatalogFieldDraft((prev) => ({ ...prev, value }))
                    }
                />
            )}
            {standaloneKind === 'shelf' && (
                <ShelfValueEditor
                    shelves={shelves}
                    shelfId={shelfDraft.shelfId}
                    shelfBinId={shelfDraft.shelfBinId}
                    onChange={setShelfDraft}
                />
            )}
            {standaloneKind === 'subflow' && (
                <TextField
                    select
                    fullWidth
                    size="small"
                    label="Published wizard to continue in"
                    value={standaloneValue || ''}
                    onChange={(e) => setStandaloneValue(e.target.value)}
                >
                    <MenuItem value="">
                        <em>Select tree…</em>
                    </MenuItem>
                    {wizardOptions.map((t) => (
                        <MenuItem key={t._id} value={t._id}>
                            {t.name}
                        </MenuItem>
                    ))}
                </TextField>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    disabled={
                        (standaloneKind === 'catalogField' &&
                            (!catalogFieldDraft.name ||
                                !catalogFieldHasValue(catalogFieldDraft.value))) ||
                        (standaloneKind === 'shelf' && !shelfDraftReady(shelfDraft))
                    }
                    onClick={() => {
                        if (standaloneKind === 'question') {
                            onAddQuestion?.();
                            return;
                        }
                        if (standaloneKind === 'catalogField') {
                            onAddAction?.('catalogField', '', {
                                catalogFieldName: catalogFieldDraft.name,
                                value: catalogFieldDraft.value,
                            });
                            setCatalogFieldDraft({ name: '', value: '' });
                            return;
                        }
                        if (standaloneKind === 'shelf') {
                            onAddAction?.('shelf', '', { ...shelfDraft });
                            setShelfDraft({ shelfId: '', shelfBinId: '', shelfName: '' });
                            return;
                        }
                        onAddAction?.(standaloneKind, standaloneValue || '');
                        setStandaloneValue(null);
                    }}
                >
                    Add
                </Button>
            </div>
        </div>
    );

    if (!node) {
        return (
            <div className="insp-inspector">
                {createToolbar}
                <p className="insp-meta" style={{ marginTop: 16 }}>
                    Select a node in the flow to edit it, or add above.
                </p>
            </div>
        );
    }

    // Pass patches only so sequential updates (e.g. catalog field + value) merge
    // against the latest node instead of racing two stale full-node writes.
    const update = (patch) => onChangeNode(selectedId, patch);

    const confirmPending = () => {
        if (pendingBranch == null || !pendingKind) return;
        if (pendingKind === 'productGrade') {
            onLinkBranchToNewResult?.(selectedId, pendingBranch, pendingValue || '');
        } else if (pendingKind === 'catalogField') {
            onLinkBranchToNewAction?.(selectedId, pendingBranch, pendingKind, '', {
                catalogFieldName: catalogFieldDraft.name,
                value: catalogFieldDraft.value,
            });
        } else if (pendingKind === 'shelf') {
            onLinkBranchToNewAction?.(selectedId, pendingBranch, pendingKind, '', {
                ...shelfDraft,
            });
        } else {
            onLinkBranchToNewAction?.(selectedId, pendingBranch, pendingKind, pendingValue || '');
        }
        setPendingBranch(null);
        setPendingKind(null);
        setPendingValue(null);
        setCatalogFieldDraft({ name: '', value: '' });
        setShelfDraft({ shelfId: '', shelfBinId: '', shelfName: '' });
    };

    const updateBranch = (index, nextNodeId) => {
        if (nextNodeId === NEW_QUESTION) {
            onLinkBranchToNewQuestion?.(selectedId, index);
            return;
        }
        if (nextNodeId === NEW_GRADE) {
            setPendingBranch(index);
            setPendingKind('productGrade');
            setPendingValue(null);
            return;
        }
        if (nextNodeId === NEW_VARIANT) {
            setPendingBranch(index);
            setPendingKind('variant');
            setPendingValue(null);
            return;
        }
        if (nextNodeId === NEW_LISTING) {
            setPendingBranch(index);
            setPendingKind('listingField');
            setPendingValue(null);
            return;
        }
        if (nextNodeId === NEW_ADD_TO_BUNDLE) {
            setPendingBranch(index);
            setPendingKind('addToBundle');
            setPendingValue(null);
            return;
        }
        if (nextNodeId === NEW_CATALOG_FIELD) {
            setPendingBranch(index);
            setPendingKind('catalogField');
            setPendingValue(null);
            setCatalogFieldDraft({ name: '', value: '' });
            return;
        }
        if (nextNodeId === NEW_SHELF) {
            setPendingBranch(index);
            setPendingKind('shelf');
            setPendingValue(null);
            setShelfDraft({ shelfId: '', shelfBinId: '', shelfName: '' });
            return;
        }
        if (nextNodeId === NEW_SUBFLOW) {
            setPendingBranch(index);
            setPendingKind('subflow');
            setPendingValue(null);
            return;
        }
        const branches = (node.branches || []).map((b, i) =>
            i === index ? { ...b, nextNodeId } : b
        );
        update({ branches });
    };

    const syncBranchesFromOptions = (options) => {
        const prev = node.branches || [];
        const byValue = new Map(prev.map((b) => [String(b.value), b]));
        const branches = options.map((opt) => {
            const existing = byValue.get(String(opt));
            return existing
                ? { ...existing, value: opt, label: String(opt) }
                : { value: opt, label: String(opt), nextNodeId: '' };
        });
        update({ options, branches, inputType: 'single_choice' });
    };

    const addAnswer = async (raw) => {
        const label = typeof raw === 'string' ? raw.trim() : String(raw || '').trim();
        if (!label) return;
        const options = uniqueOptions([...(node.options || []).map(String), label]);
        syncBranchesFromOptions(options);
        if (saveForReuse) {
            try {
                await api.post('/api/broylesllc/inspection-trees/meta/step-options', {
                    label,
                    category: categoryScope || '',
                    treeId: tree?._id || '',
                });
                refetchSteps?.();
            } catch {
                /* ignore */
            }
        }
        setNewAnswer(null);
        setStepQuery('');
    };

    const removeAnswer = (value) => {
        const options = (node.options || []).map(String).filter((o) => o !== String(value));
        syncBranchesFromOptions(options);
    };

    const reorderAnswer = (fromIndex, toIndex) => {
        if (
            fromIndex == null ||
            toIndex == null ||
            fromIndex === toIndex ||
            fromIndex < 0 ||
            toIndex < 0
        ) {
            return;
        }
        const options = (node.options || []).map(String);
        if (fromIndex >= options.length || toIndex >= options.length) return;
        const next = [...options];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        syncBranchesFromOptions(next);
    };

    /* ——— ACTION / TERMINAL EDITOR ——— */
    if (node.type === 'action' || node.type === 'terminal') {
        const kind =
            node.type === 'terminal' ? 'productGrade' : node.actionKind || 'productGrade';
        const value =
            node.value ||
            node.result?.productGrade ||
            node.result?.condition ||
            node.subflowTreeId ||
            '';
        const continues = Boolean(node.nextNodeId);

        return (
            <div className="insp-inspector">
                {createToolbar}
                <div className="type-label">
                    {kind === 'variant'
                        ? 'Result · Variant'
                        : kind === 'listingField'
                          ? 'Result · Reassign SKU'
                          : kind === 'addToBundle'
                            ? 'Result · Add to bundle'
                            : kind === 'catalogField'
                              ? 'Result · Catalog field'
                              : kind === 'shelf'
                                ? 'Result · Shelf'
                                : kind === 'subflow'
                                  ? 'Wizard handoff'
                                  : 'Result · Product Grade'}
                    {continues ? ' · continues' : ' · ends path'}
                </div>
                <h2>{node.title || value || 'Result'}</h2>

                <div className="insp-section">
                    <TextField
                        fullWidth
                        label="Title"
                        size="small"
                        margin="dense"
                        value={node.title || ''}
                        onChange={(e) => update({ title: e.target.value })}
                    />

                    {kind === 'productGrade' && (
                        <Autocomplete
                            options={gradeOptions}
                            value={value || null}
                            getOptionLabel={(o) => String(o ?? '')}
                            onChange={(_, v) => {
                                const next = v || '';
                                update({
                                    type: 'action',
                                    actionKind: 'productGrade',
                                    title: next || node.title || 'Product Grade',
                                    value: next,
                                    result: {
                                        ...(node.result || {}),
                                        condition: next,
                                        productGrade: next,
                                    },
                                });
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Product Grade"
                                    size="small"
                                    margin="dense"
                                    helperText="Written to catalog Product Grade"
                                    required
                                    error={!value}
                                />
                            )}
                        />
                    )}

                    {kind === 'variant' && (
                        <p className="hint">
                            During the wizard, the operator picks from this Product&apos;s variant
                            cards to set the catalog item variant. No preset value needed here.
                        </p>
                    )}

                    {kind === 'listingField' && (
                        <p className="hint">
                            During the wizard, the operator gets a SKU autocomplete to reassign this
                            Item ID to the correct Products · Listing Field.
                        </p>
                    )}

                    {kind === 'addToBundle' && (
                        <p className="hint">
                            During the wizard, the operator picks a Product SKU, then chooses an
                            existing bundle catalog item for that SKU or creates a new one.
                        </p>
                    )}

                    {kind === 'catalogField' && (
                        <CatalogFieldValueEditor
                            fields={catalogFieldOptions}
                            fieldName={node.catalogFieldName || ''}
                            value={node.value ?? ''}
                            onFieldChange={(name) => {
                                const field = catalogFieldOptions.find((f) => f.name === name);
                                update({
                                    type: 'action',
                                    actionKind: 'catalogField',
                                    catalogFieldName: name,
                                    value: field?.type === 'multipleSelects' ? [] : '',
                                    title: name || node.title || 'Catalog field',
                                });
                            }}
                            onValueChange={(nextValue) =>
                                update({
                                    type: 'action',
                                    actionKind: 'catalogField',
                                    value: nextValue,
                                    title: node.catalogFieldName || node.title || 'Catalog field',
                                })
                            }
                        />
                    )}

                    {kind === 'shelf' && (
                        <ShelfValueEditor
                            shelves={shelves}
                            shelfId={node.shelfId || ''}
                            shelfBinId={node.shelfBinId || ''}
                            onChange={({ shelfId, shelfBinId, shelfName }) =>
                                update({
                                    type: 'action',
                                    actionKind: 'shelf',
                                    shelfId,
                                    shelfBinId,
                                    shelfName,
                                    title: shelfName || node.title || 'Shelf',
                                })
                            }
                        />
                    )}

                    {kind === 'subflow' && (
                        <TextField
                            select
                            fullWidth
                            size="small"
                            margin="dense"
                            label="Published wizard"
                            value={node.subflowTreeId || ''}
                            onChange={(e) =>
                                update({
                                    subflowTreeId: e.target.value,
                                    title:
                                        wizardOptions.find((t) => t._id === e.target.value)?.name ||
                                        node.title,
                                })
                            }
                            required
                            error={!node.subflowTreeId}
                        >
                            <MenuItem value="">
                                <em>Select…</em>
                            </MenuItem>
                            {wizardOptions.map((t) => (
                                <MenuItem key={t._id} value={t._id}>
                                    {t.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}

                    {kind !== 'subflow' && (
                        <>
                            <TextField
                                fullWidth
                                label="Instructions (optional)"
                                size="small"
                                margin="dense"
                                multiline
                                minRows={2}
                                value={node.result?.instructions || ''}
                                onChange={(e) =>
                                    update({
                                        result: { ...(node.result || {}), instructions: e.target.value },
                                    })
                                }
                            />
                            <FormControlLabel
                                sx={{ mt: 1 }}
                                control={
                                    <Switch
                                        checked={Boolean(node.result?.requiresPhotos)}
                                        onChange={(e) =>
                                            update({
                                                result: {
                                                    ...(node.result || {}),
                                                    requiresPhotos: e.target.checked,
                                                },
                                            })
                                        }
                                    />
                                }
                                label="Require photos"
                            />
                        </>
                    )}
                </div>

                <div className="insp-section">
                    <h4>Then continue?</h4>
                    <p className="hint">
                        Leave empty to end the inspection after this assignment. Or link to another
                        question / result to keep processing.
                    </p>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Next step"
                        value={node.nextNodeId || ''}
                        onChange={(e) => {
                            const v = e.target.value;
                            if (v === NEW_QUESTION) {
                                onLinkActionToNewQuestion?.(selectedId);
                                return;
                            }
                            if (v === NEW_GRADE) {
                                onLinkActionToNewAction?.(selectedId, 'productGrade', '');
                                return;
                            }
                            if (v === NEW_VARIANT) {
                                onLinkActionToNewAction?.(selectedId, 'variant', '');
                                return;
                            }
                            if (v === NEW_LISTING) {
                                onLinkActionToNewAction?.(selectedId, 'listingField', '');
                                return;
                            }
                            if (v === NEW_ADD_TO_BUNDLE) {
                                onLinkActionToNewAction?.(selectedId, 'addToBundle', '');
                                return;
                            }
                            if (v === NEW_CATALOG_FIELD) {
                                onLinkActionToNewAction?.(selectedId, 'catalogField', '', {
                                    catalogFieldName: '',
                                    value: '',
                                });
                                return;
                            }
                            if (v === NEW_SHELF) {
                                onLinkActionToNewAction?.(selectedId, 'shelf', '', {
                                    shelfId: '',
                                    shelfBinId: '',
                                    shelfName: '',
                                });
                                return;
                            }
                            if (v === selectedId) return;
                            update({ nextNodeId: v });
                        }}
                    >
                        <MenuItem value="">
                            <em>End path here</em>
                        </MenuItem>
                        <MenuItem value={NEW_QUESTION}>+ New question…</MenuItem>
                        <MenuItem value={NEW_GRADE}>+ New result (Product Grade)…</MenuItem>
                        <MenuItem value={NEW_VARIANT}>+ New result (Variant)…</MenuItem>
                        <MenuItem value={NEW_LISTING}>+ New result (Reassign SKU)…</MenuItem>
                        <MenuItem value={NEW_ADD_TO_BUNDLE}>+ New result (Add to bundle)…</MenuItem>
                        <MenuItem value={NEW_CATALOG_FIELD}>+ New result (Catalog field)…</MenuItem>
                        <MenuItem value={NEW_SHELF}>+ New result (Shelf)…</MenuItem>
                        <MenuItem disabled value="__q">
                            — Questions —
                        </MenuItem>
                        {questionOptions
                            .filter((q) => q.id !== selectedId)
                            .map((q) => (
                                <MenuItem key={q.id} value={q.id}>
                                    {nodeLabel(q)}
                                </MenuItem>
                            ))}
                        <MenuItem disabled value="__a">
                            — Results —
                        </MenuItem>
                        {actionOptions
                            .filter((a) => a.id !== selectedId)
                            .map((a) => (
                                <MenuItem key={a.id} value={a.id}>
                                    {nodeLabel(a)}
                                </MenuItem>
                            ))}
                    </TextField>
                    {node.nextNodeId && nodes[node.nextNodeId] && (
                        <Button
                            size="small"
                            sx={{ mt: 1 }}
                            onClick={() => onSelect(node.nextNodeId)}
                        >
                            Open next node
                        </Button>
                    )}
                </div>
            </div>
        );
    }

    /* ——— QUESTION EDITOR ——— */
    const isStart = tree.startNodeId === selectedId;

    return (
        <div className="insp-inspector">
            {createToolbar}
            <div className="type-label">
                Question{isStart ? ' · Starts the inspection' : ''}
            </div>
            <h2>{node.title || 'Question'}</h2>

            <div className="insp-section">
                <h4>Title</h4>
                <TextField
                    fullWidth
                    size="small"
                    value={node.title || ''}
                    onChange={(e) => update({ title: e.target.value })}
                />
            </div>

            <div className="insp-section">
                <h4>Ask the operator</h4>
                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    value={node.prompt || ''}
                    onChange={(e) => update({ prompt: e.target.value })}
                />
            </div>

            <div className="insp-section">
                <h4>Answer choices</h4>
                <p className="hint">
                    Autocomplete from saved steps for this category/tree. Drag to reorder. Saving for
                    reuse is opt-in.
                </p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                    <Autocomplete
                        freeSolo
                        fullWidth
                        options={answerOptions}
                        value={newAnswer ?? null}
                        inputValue={stepQuery ?? ''}
                        onInputChange={(_, v) => setStepQuery(String(v ?? ''))}
                        onChange={(_, v) => setNewAnswer(v)}
                        getOptionLabel={(option) => String(option ?? '')}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                label="Add answer"
                                placeholder="e.g. Minor Scratches"
                            />
                        )}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => addAnswer(newAnswer || stepQuery)}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        Add
                    </Button>
                </div>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={saveForReuse}
                            onChange={(e) => setSaveForReuse(e.target.checked)}
                            size="small"
                        />
                    }
                    label="Save this answer for reuse (category / tree library)"
                />
                {(node.options || []).map((opt, index) => (
                    <div
                        key={String(opt)}
                        className={[
                            'insp-choice-row',
                            dragAnswerIndex === index ? 'is-dragging' : '',
                            dragAnswerIndex != null && dragAnswerIndex !== index
                                ? 'is-drop-target'
                                : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                        }}
                        onDrop={(e) => {
                            e.preventDefault();
                            const fromRaw = e.dataTransfer.getData('text/plain');
                            const fromIndex =
                                fromRaw !== '' ? Number(fromRaw) : dragAnswerIndex;
                            reorderAnswer(fromIndex, index);
                            setDragAnswerIndex(null);
                        }}
                    >
                        <span
                            className="insp-choice-drag"
                            title="Drag to reorder"
                            draggable
                            onDragStart={(e) => {
                                setDragAnswerIndex(index);
                                e.dataTransfer.effectAllowed = 'move';
                                e.dataTransfer.setData('text/plain', String(index));
                            }}
                            onDragEnd={() => setDragAnswerIndex(null)}
                        >
                            <DragIndicatorIcon fontSize="small" />
                        </span>
                        <span className="insp-choice-label">{String(opt)}</span>
                        <IconButton size="small" onClick={() => removeAnswer(opt)}>
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </div>
                ))}
            </div>

            <div className="insp-section">
                <h4>What happens for each answer</h4>
                <p className="hint">
                    Link to a next question, assign a result (grade / variant / rename), hand off to
                    another wizard, or insert a step between existing links.
                </p>
                {(node.branches || []).map((branch, index) => (
                    <div key={index} className="insp-branch-row">
                        <div className="if-label">If {branchDisplay(branch, node.inputType)}</div>

                        {!branch.nextNodeId && pendingBranch !== index && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => onLinkBranchToNewQuestion?.(selectedId, index)}
                                >
                                    Add next question
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                        setPendingBranch(index);
                                        setPendingKind('productGrade');
                                        setPendingValue(null);
                                    }}
                                >
                                    Add grade
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                        setPendingBranch(index);
                                        setPendingKind('variant');
                                        setPendingValue(null);
                                    }}
                                >
                                    Add variant
                                </Button>
                            </div>
                        )}

                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Or link to…"
                            value={
                                pendingBranch === index
                                    ? pendingKind === 'variant'
                                        ? NEW_VARIANT
                                        : pendingKind === 'listingField'
                                          ? NEW_LISTING
                                          : pendingKind === 'addToBundle'
                                            ? NEW_ADD_TO_BUNDLE
                                            : pendingKind === 'catalogField'
                                              ? NEW_CATALOG_FIELD
                                              : pendingKind === 'shelf'
                                                ? NEW_SHELF
                                                : pendingKind === 'subflow'
                                                  ? NEW_SUBFLOW
                                                  : NEW_GRADE
                                    : branch.nextNodeId || ''
                            }
                            onChange={(e) => updateBranch(index, e.target.value)}
                        >
                            <MenuItem value="">
                                <em>Not linked yet</em>
                            </MenuItem>
                            <MenuItem value={NEW_QUESTION}>+ New question…</MenuItem>
                            <MenuItem value={NEW_GRADE}>+ New result (Product Grade)…</MenuItem>
                            <MenuItem value={NEW_VARIANT}>+ New result (Variant)…</MenuItem>
                            <MenuItem value={NEW_LISTING}>+ New result (Reassign SKU)…</MenuItem>
                            <MenuItem value={NEW_ADD_TO_BUNDLE}>+ New result (Add to bundle)…</MenuItem>
                            <MenuItem value={NEW_CATALOG_FIELD}>+ New result (Catalog field)…</MenuItem>
                            <MenuItem value={NEW_SHELF}>+ New result (Shelf)…</MenuItem>
                            <MenuItem value={NEW_SUBFLOW}>+ New wizard…</MenuItem>
                            <MenuItem disabled value="__q">
                                — Questions —
                            </MenuItem>
                            {questionOptions
                                .filter((q) => q.id !== selectedId)
                                .map((q) => (
                                    <MenuItem key={q.id} value={q.id}>
                                        {nodeLabel(q)}
                                    </MenuItem>
                                ))}
                            <MenuItem disabled value="__a">
                                — Results / actions —
                            </MenuItem>
                            {actionOptions.map((r) => (
                                <MenuItem key={r.id} value={r.id}>
                                    {nodeLabel(r)}
                                </MenuItem>
                            ))}
                        </TextField>

                        {pendingBranch === index && (
                            <div style={{ marginTop: 10 }}>
                                {pendingKind === 'productGrade' && (
                                    <Autocomplete
                                        options={gradeOptions}
                                        value={pendingValue}
                                        onChange={(_, v) => setPendingValue(v)}
                                        getOptionLabel={(o) => String(o ?? '')}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                label="Product Grade"
                                                autoFocus
                                            />
                                        )}
                                    />
                                )}
                                {pendingKind === 'variant' && (
                                    <p className="hint" style={{ margin: '0 0 8px' }}>
                                        Operator will choose from this Product&apos;s variant cards
                                        in the wizard.
                                    </p>
                                )}
                                {pendingKind === 'listingField' && (
                                    <p className="hint" style={{ margin: '0 0 8px' }}>
                                        Operator will choose the correct SKU in the wizard.
                                    </p>
                                )}
                                {pendingKind === 'addToBundle' && (
                                    <p className="hint" style={{ margin: '0 0 8px' }}>
                                        Operator will pick a Product SKU and bundle target in the
                                        wizard.
                                    </p>
                                )}
                                {pendingKind === 'catalogField' && (
                                    <CatalogFieldValueEditor
                                        fields={catalogFieldOptions}
                                        fieldName={catalogFieldDraft.name}
                                        value={catalogFieldDraft.value}
                                        onFieldChange={(name) => {
                                            const field = catalogFieldOptions.find((f) => f.name === name);
                                            setCatalogFieldDraft({
                                                name,
                                                value: field?.type === 'multipleSelects' ? [] : '',
                                            });
                                        }}
                                        onValueChange={(value) =>
                                            setCatalogFieldDraft((prev) => ({ ...prev, value }))
                                        }
                                    />
                                )}
                                {pendingKind === 'shelf' && (
                                    <ShelfValueEditor
                                        shelves={shelves}
                                        shelfId={shelfDraft.shelfId}
                                        shelfBinId={shelfDraft.shelfBinId}
                                        onChange={setShelfDraft}
                                    />
                                )}
                                {pendingKind === 'subflow' && (
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Published wizard"
                                        value={pendingValue || ''}
                                        onChange={(e) => setPendingValue(e.target.value)}
                                    >
                                        {wizardOptions.map((t) => (
                                            <MenuItem key={t._id} value={t._id}>
                                                {t.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={
                                            (pendingKind === 'catalogField' &&
                                                (!catalogFieldDraft.name ||
                                                    !catalogFieldHasValue(
                                                        catalogFieldDraft.value
                                                    ))) ||
                                            (pendingKind === 'shelf' &&
                                                !shelfDraftReady(shelfDraft))
                                        }
                                        onClick={confirmPending}
                                    >
                                        Create
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            setPendingBranch(null);
                                            setPendingKind(null);
                                            setPendingValue(null);
                                            setCatalogFieldDraft({ name: '', value: '' });
                                            setShelfDraft({
                                                shelfId: '',
                                                shelfBinId: '',
                                                shelfName: '',
                                            });
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}

                        {pendingBranch !== index && branch.nextNodeId && nodes[branch.nextNodeId] && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => onSelect(branch.nextNodeId)}
                                >
                                    Open linked node
                                </Button>
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => onInsertQuestionBetween?.(selectedId, index)}
                                >
                                    Insert question here
                                </Button>
                                <Button size="small" onClick={() => updateBranch(index, '')}>
                                    Unlink
                                </Button>
                            </div>
                        )}
                    </div>
                ))}
                {(node.branches || []).length === 0 && (
                    <p className="insp-meta">Add answer choices above first.</p>
                )}
            </div>
        </div>
    );
}
